import React, { FC } from 'react';

import { useTranslation } from 'react-i18next';

import { ButtonLoadable } from '@/components/ui/button-loadable';

import {
  Integration,
  IntegrationServices,
  IntegrationState,
} from '../../../../common/types/integrations';
import { getServiceIcon } from '../../helpers/data';

interface IntegrationsRowProps {
  integration: Integration;
  onReasons: (service: IntegrationServices) => void;
  onConnect: (service: IntegrationServices) => void;
}

export const IntegrationsRow: FC<IntegrationsRowProps> = ({
  integration: { service, dataTypes, state },
  onReasons,
  onConnect,
}) => {
  const { t } = useTranslation(['authenticated.onboarding.integrations']);

  const getButtonLabel = () => {
    switch (state) {
      case IntegrationState.NOT_CONNECTED:
        return t('integrationSection.actions.connect');
      case IntegrationState.SYNCING:
        return t('integrationSection.actions.loading');
      case IntegrationState.CONNECTED:
        return t('integrationSection.actions.success');
      case IntegrationState.ERROR_CONNECTION:
        return t('integrationSection.actions.reconnect');
      default:
        return '';
    }
  };

  const renderActionButton = () => {
    return (
      <ButtonLoadable
        type="button"
        size="sm"
        variant={state === IntegrationState.CONNECTED ? 'success' : 'outline'}
        isLoading={state === IntegrationState.SYNCING}
        disabled={state === IntegrationState.CONNECTED}
        onClick={() => onConnect(service)}
      >
        {getButtonLabel()}
      </ButtonLoadable>
    );
  };

  return (
    <tr className="border-b border-input">
      <td className="px-4 py-2">
        <div className="flex items-center gap-2">
          <img alt={service} src={getServiceIcon(service)} className="size-6" />

          <div>
            <p className="font-medium">{service}</p>

            {state === IntegrationState.ERROR_CONNECTION && (
              <p className="text-xs text-destructive">
                {t('integrationSection.messages.connectionFailed')}{' '}
                <a href="#" className="underline" onClick={() => onReasons(service)}>
                  {t('integrationSection.messages.checkReasons')}
                </a>
              </p>
            )}
          </div>
        </div>
      </td>

      <td className="px-4 py-2">
        {dataTypes.map(dataType => t(`dataTypes.${dataType}`)).join(', ')}
      </td>

      <td className="px-4 py-2 text-right">{renderActionButton()}</td>
    </tr>
  );
};
